import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, CurrencyEur } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { saCard, saInput, saLabel, fmtD, SABadge, ACCOUNT_STATUS, PAYMENT_STATUS } from "../../components/saShared";

export default function SARistoranteDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [plans, setPlans] = useState([]);
  const [note, setNote] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [ckPlan, setCkPlan] = useState("");
  const [ckInt, setCkInt] = useState("monthly");
  const [ckUrl, setCkUrl] = useState("");
  const [ckBusy, setCkBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/superadmin/restaurants/${id}`).then((res) => setR(res.data)).catch((e) => toast.error(apiError(e)));
  }, [id]);

  useEffect(() => {
    load();
    api.get("/superadmin/plans").then((res) => setPlans(res.data)).catch(() => {});
  }, [load]);

  if (!r) return <div className="text-sm text-zinc-500 py-10">Caricamento...</div>;

  const action = async (act) => {
    try { await api.post(`/superadmin/restaurants/${id}/action`, { action: act }); toast.success("Fatto"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const patch = async (payload, msg) => {
    try { await api.patch(`/superadmin/restaurants/${id}`, payload); toast.success(msg); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const mockPay = async (outcome) => {
    try {
      const res = await api.post(`/superadmin/restaurants/${id}/mock-payment`, { outcome });
      toast.success(`Pagamento ${outcome === "success" ? "riuscito" : "fallito"} simulato → stato: ${res.data.account_status}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const genCheckout = async () => {
    setCkBusy(true);
    try {
      await api.post("/superadmin/stripe/sync-catalog").catch(() => {});
      const res = await api.post(`/superadmin/restaurants/${id}/checkout-link`, { plan_id: ckPlan, interval: ckInt });
      setCkUrl(res.data.checkout_url);
      toast.success("Link pagamento Stripe generato");
    } catch (e) { toast.error(apiError(e)); } finally { setCkBusy(false); }
  };

  const btnCls = "h-10 px-4 rounded-md text-xs font-bold transition active:scale-95";

  return (
    <div className="space-y-5" data-testid="sa-ristorante-dettaglio">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate("/superadmin/ristoranti")} data-testid="sa-back"
          className="p-2 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-400"><ArrowLeft size={18} /></button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl font-bold tracking-tight text-white truncate">{r.name}</h1>
          <p className="text-xs text-zinc-500">{r.phone || "—"} · {r.users_count} utenti</p>
        </div>
        <SABadge map={ACCOUNT_STATUS} value={r.account_status} />
      </div>

      <div className="flex gap-2 flex-wrap">
        {r.account_status === "trial" && (
          <button onClick={() => action("attiva")} data-testid="sa-det-attiva" className={`${btnCls} bg-emerald-500/20 text-emerald-300 border border-emerald-500/40`}>ATTIVA</button>
        )}
        {["attivo", "trial", "tolleranza", "pagamento_fallito"].includes(r.account_status) && (
          <button onClick={() => action("sospendi")} data-testid="sa-det-sospendi" className={`${btnCls} bg-rose-500/15 text-rose-300 border border-rose-500/40`}>SOSPENDI</button>
        )}
        {r.account_status === "sospeso" && (
          <button onClick={() => action("riattiva")} data-testid="sa-det-riattiva" className={`${btnCls} bg-amber-400/15 text-amber-300 border border-amber-400/40`}>RIATTIVA</button>
        )}
        <div className="flex-1" />
        <button onClick={() => mockPay("success")} data-testid="sa-mock-success"
          className={`${btnCls} bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 flex items-center gap-1.5`}>
          <CheckCircle size={14} weight="bold" /> MOCK pagamento OK
        </button>
        <button onClick={() => mockPay("failed")} data-testid="sa-mock-failed"
          className={`${btnCls} bg-rose-500/15 text-rose-300 border border-rose-500/40 flex items-center gap-1.5`}>
          <XCircle size={14} weight="bold" /> MOCK pagamento KO
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <section className={`${saCard} p-4 space-y-2`}>
          <h2 className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Account e abbonamento</h2>
          <div className="text-sm text-zinc-300">Piano: <strong className="text-white">{r.plan_name}</strong></div>
          <div className="text-sm text-zinc-300 capitalize">Subscription: {(r.subscription_status || "—").replaceAll("_", " ")}</div>
          <div className="text-sm text-zinc-300">Attivazione: {fmtD(r.activated_at)}</div>
          <div className="text-sm text-zinc-300">Fine prova: {fmtD(r.trial_ends_at)}</div>
          <div className="text-sm text-zinc-300">Scadenza: {fmtD(r.expires_at)}</div>
          <div className="text-sm text-zinc-300">Prossimo rinnovo: {fmtD(r.next_payment_at)}</div>
          {r.grace_ends_at && <div className="text-sm text-amber-300">Tolleranza fino al: {fmtD(r.grace_ends_at)}</div>}
          {r.stripe && (r.stripe.customer_id || r.stripe.subscription_id) && (
            <div className="text-xs text-zinc-500 pt-1 border-t border-zinc-800">
              Stripe: {r.stripe.customer_id || "—"} / {r.stripe.subscription_id || "—"}
            </div>
          )}
          <div className="pt-2 border-t border-zinc-800 space-y-2">
            <label className={saLabel}>Cambia piano</label>
            <select value={r.plan_id || ""} onChange={(e) => patch({ plan_id: e.target.value }, "Piano aggiornato")}
              className={saInput} data-testid="sa-det-plan">
              <option value="">Nessuno</option>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — €{p.prezzo_mensile}/mese</option>)}
            </select>
            <div className="flex gap-1.5 flex-wrap">
              {[7, 14, 30].map((d) => (
                <button key={d} onClick={() => patch({ extend_trial_days: d }, `Prova estesa di ${d} giorni`)}
                  data-testid={`sa-extend-${d}`}
                  className="h-8 px-2.5 rounded-md bg-zinc-800 text-zinc-300 text-[11px] font-semibold">+{d}gg prova</button>
              ))}
            </div>
            <div className="flex gap-1.5">
              <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className={saInput} data-testid="sa-expires" />
              <button onClick={() => expiresAt && patch({ expires_at: expiresAt }, "Scadenza impostata")}
                data-testid="sa-expires-set"
                className="h-11 px-3 rounded-md bg-zinc-800 text-zinc-200 text-xs font-bold shrink-0">Imposta</button>
            </div>
            <div className="pt-2 border-t border-zinc-800 space-y-1.5">
              <label className={saLabel}>Link pagamento Stripe (reale)</label>
              <div className="flex gap-1.5">
                <select value={ckPlan} onChange={(e) => setCkPlan(e.target.value)} className={saInput} data-testid="sa-ck-plan">
                  <option value="">Piano…</option>
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — €{p.prezzo_mensile}/mese</option>)}
                </select>
                <select value={ckInt} onChange={(e) => setCkInt(e.target.value)} className={saInput} data-testid="sa-ck-interval">
                  <option value="monthly">Mensile</option>
                  <option value="yearly">Annuale</option>
                </select>
              </div>
              <button onClick={genCheckout} disabled={!ckPlan || ckBusy} data-testid="sa-ck-generate"
                className="h-9 px-3 rounded-md bg-sky-500/20 text-sky-300 border border-sky-500/40 text-xs font-bold disabled:opacity-40">
                {ckBusy ? "Generazione…" : "Genera link pagamento"}
              </button>
              {ckUrl && (
                <div className="flex gap-1.5">
                  <input readOnly value={ckUrl} onFocus={(e) => e.target.select()} className={saInput} data-testid="sa-ck-url" />
                  <button onClick={() => { navigator.clipboard.writeText(ckUrl); toast.success("Link copiato"); }}
                    data-testid="sa-ck-copy"
                    className="h-9 px-3 rounded-md bg-zinc-800 text-zinc-200 text-xs font-bold shrink-0">Copia</button>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className={`${saCard} p-4 space-y-2`}>
          <h2 className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Referente e utenti</h2>
          <div className="text-sm text-zinc-300"><strong className="text-white">{r.referente?.name}</strong></div>
          <div className="text-sm text-zinc-400">{r.referente?.email}</div>
          <div className="pt-2 border-t border-zinc-800 space-y-1.5" data-testid="sa-det-users">
            {r.users.map((u) => (
              <div key={u.id} className="flex items-center justify-between text-sm">
                <span className="text-zinc-300">{u.name}</span>
                <span className="text-xs text-zinc-500 capitalize">{u.role}</span>
              </div>
            ))}
          </div>
          <div className="pt-2 border-t border-zinc-800">
            <label className={saLabel}>Note amministrative</label>
            <div className="space-y-1.5 mb-2 max-h-32 overflow-y-auto">
              {(r.admin_notes || []).map((n, i) => (
                <div key={i} className="text-xs text-zinc-400 bg-zinc-800/60 rounded p-2">
                  {n.text} <span className="text-zinc-600">— {fmtD(n.at)}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-1.5">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Aggiungi nota interna..."
                className={saInput} data-testid="sa-note" />
              <button onClick={() => note.trim() && patch({ note: note.trim() }, "Nota aggiunta") || setNote("")}
                data-testid="sa-note-add"
                className="h-11 px-3 rounded-md bg-zinc-800 text-zinc-200 text-xs font-bold shrink-0">Salva</button>
            </div>
          </div>
        </section>

        <section className={`${saCard} p-4`}>
          <h2 className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">Storico modifiche account</h2>
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="sa-det-events">
            {r.events.length === 0 && <div className="text-xs text-zinc-500">Nessun evento</div>}
            {r.events.map((e) => (
              <div key={e.id} className="text-xs border-l-2 border-zinc-700 pl-2">
                <div className="font-semibold text-zinc-200">{e.type.replaceAll("_", " ")}</div>
                <div className="text-zinc-500">{e.note}</div>
                <div className="text-zinc-600">{fmtD(e.at)} · {e.by}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className={saCard}>
        <h2 className="px-4 py-3 border-b border-zinc-800 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
          <CurrencyEur size={13} /> Storico pagamenti
        </h2>
        <div className="divide-y divide-zinc-800/60" data-testid="sa-det-payments">
          {r.payments.length === 0 && <div className="px-4 py-6 text-sm text-zinc-500">Nessun pagamento registrato</div>}
          {r.payments.map((p) => (
            <div key={p.id} className="flex items-center gap-3 px-4 py-3 flex-wrap">
              <span className="text-sm font-semibold text-zinc-100">€{p.amount} {p.currency?.toUpperCase()}</span>
              <SABadge map={PAYMENT_STATUS} value={p.status} />
              {p.mock && <span className="text-[10px] font-bold text-zinc-500 border border-zinc-700 rounded-full px-1.5 py-0.5">MOCK</span>}
              <span className="text-xs text-zinc-500">{p.invoice}</span>
              {p.stripe_payment_intent && <span className="text-xs text-zinc-500 font-mono">{p.stripe_payment_intent}</span>}
              {p.error && <span className="text-xs text-rose-400">{p.error}</span>}
              <span className="text-xs text-zinc-500 ml-auto">{fmtD(p.created_at)}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
